<template>
  <div class="research-group-manage">
    <div v-if="!canEditRef" class="alert alert-primary alert-sm">
      <LockIcon size="1.2x"/> You do not have permission to manage this tree.
    </div>
    <div>
      <label class="form-label">
        Owner
      </label>
      <div class="owner">
        <img src="@/assets/icons/person_gray_150.png"/>
        {{ ownerRef?.displayName }}
      </div>
    </div>
    <div v-if="lastSyncRef">
      <label class="form-label">Last updated</label>
      <div>{{ DateTimeUtil.toRelativePastDate(lastSyncRef.createdDate) }}</div>
    </div>
    <div>
      <label class="form-label">Database file</label>
      <WorkspaceUpdatePanel v-if="canUpdateRef && workspaceRef" :workspace-id="workspaceRef.id!"/>
    </div>
    <div class="actions">
      <button type="button" v-if="canDeleteRef" class="btn btn-outline-danger" data-bs-toggle="modal" data-bs-target="#deleteGroupModal" :disabled="!dataGroupRef">
        Delete online tree
      </button>
    </div>
    <Modal id="deleteGroupModal" ref="deleteGroupModalRef" ok-style="danger" @ok="deleteGroup">
      <template #title>Delete this tree?</template>
      <div>
        <p class="mb-1">This will:</p>
        <ul>
          <li>Stop sharing the tree with other users and groups</li>
          <li>Permanently delete the online copy of your tree</li>
        </ul>
      </div>
      <template #okText>
        <Trash2Icon size="1.2x"/>
        Delete online tree
      </template>
      <template #busyText>Deleting....</template>
    </Modal>
  </div>
</template>

<style lang="css" scoped>
.research-group-manage {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.rel-date {
  font-size: 0.875rem;
  color: #888;
}

.actions {
  margin-top: 2rem;
  display: flex;
  gap: 0.5rem;
}

.owner {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  img {
    width: 20px;
    opacity: 60%;
  }
}

.workspace-update-panel {
  margin-top: 0.5rem;
}
</style>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useUserStore } from '@/gp/UserStore'
import { useWorkspaceStore } from '@/rd/WorkspaceStore'
import { useSyncStore } from '@/rd/SyncStore'
import { useFeatureStore, SyncChangesFeature } from '@/rd/FeatureStore'
import { ItemPermissions } from '@/gp/GroupAdminModel'
import { LockIcon, Trash2Icon } from '@zhuowenli/vue-feather-icons'
import { LoadMode } from '@/util/AsyncData'
import { DateTimeUtil } from '@/util/LuxonUtil'
import Modal from '@/util/Modal.vue'
import WorkspaceUpdatePanel from '@/sync/WorkspaceUpdatePanel.vue'

const props = defineProps<{
  dataGroupId?: string
}>()

const deleteGroupModalRef = ref<InstanceType<typeof Modal>>()

const dataGroupStore = useDataGroupStore()
const userStore = useUserStore()
const workspaceStore = useWorkspaceStore()
const syncStore = useSyncStore()
const featureStore = useFeatureStore()
const router = useRouter()

const dataGroupRef = computed(() => dataGroupStore.getAsyncGroup(props.dataGroupId, LoadMode.EnsureLoaded)?.data)
const ownerRef = computed(() => userStore.getAsyncUser(dataGroupRef.value?.ownerId, LoadMode.EnsureLoaded)?.data)
const workspaceRef = computed(() => workspaceStore.getAsyncWorkspacesForGroups([props.dataGroupId], LoadMode.EnsureLoaded).at(0)?.data)
const lastSyncRef = computed(() => syncStore.getAsyncSync(workspaceRef.value?.lastSyncId, LoadMode.EnsureLoaded)?.data)
const syncChangesEnabledRef = computed(() => featureStore.isFeatureEnabled(SyncChangesFeature))

const permissionsRef = computed(() => dataGroupStore.getAsyncPermissions(props.dataGroupId, LoadMode.EnsureLoaded)?.data ?? ItemPermissions.None)
const canEditRef = computed(() => (permissionsRef.value & ItemPermissions.Modify) > 0)
const canUpdateRef = computed(() => syncChangesEnabledRef.value && canEditRef.value)
const canDeleteRef = computed(() => (permissionsRef.value & ItemPermissions.Owner) > 0)

async function deleteGroup() {
  await dataGroupStore.deleteAsync(dataGroupRef.value!.id!)

  deleteGroupModalRef.value!.close() // get rid of static backdrop
  router.push('/trees')
}
</script>
